import React, { useState, useEffect } from "react";
import {
  Search,
  Grid,
  List,
  RefreshCw,
  MapPin,
  Calendar,
  TrendingUp,
  Users,
  Target,
  CheckCircle,
  XCircle,
} from "lucide-react";
import "./PreSowing.css";

const Emergence = () => {
  const [viewMode, setViewMode] = useState("grid");
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalActivities: 0,
    landPrepared: 0,
    notPrepared: 0,
    activeFarmers: 0,
   
  });

  const API_URL = " https://sadaatagrofarms.com/api/crop_stages/emergence/list/";

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    fetch(API_URL, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = data.results || data;
        setActivities(list);

        // ------------------------------
        // Auto Stats Calculation
        // ------------------------------
        setStats({
          totalActivities: list.length,
          landPrepared: list.filter((x) => x.weeds_present === true).length,
          notPrepared: list.filter((x) => x.weeds_present === false).length,
          activeFarmers: new Set(list.map((x) => x.farmer)).size,
         
        });
      })
      .catch((err) => console.error("Error fetching emergence data:", err));
  }, []);

  // -----------------------------------
  // FILTER + SEARCH
  // -----------------------------------
  const filteredActivities = activities.filter((item) => {
    let matchesSearch =
      item.weed_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.crop_health?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.percentage_weed_cover + "").includes(searchQuery);

    let matchesStatus =
      filterStatus === "All"
        ? true
        : filterStatus === "Prepared"
        ? item.weeds_present === true
        : item.weeds_present === false;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="pre-sowing">
      {/* Stage Tabs */}
      <div className="stage-tabs">
        <button className="stage-tab">Pre-Sowing</button>
        <button className="stage-tab">Sowing & Emergence</button>
        <button className="stage-tab active">Emergence</button>
        <button className="stage-tab">Growth & Tillering</button>
        <button className="stage-tab">Booting Stage</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Emergence Activities Dashboard</h1>
          <p className="subtitle">Monitor and analyze emergence stage activities</p>
        </div>
        <button className="refresh-btn">
          <RefreshCw size={16} />
          Refresh Data
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Records</div>
            <div className="stat-sublabel">{stats.totalActivities} entries</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Weeds Present</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red"><XCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value danger">{stats.notPrepared}</div>
            <div className="stat-label">No Weeds</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Unique Farmers</div>
          </div>
        </div>

      
      </div>

      {/* Filters */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by weed type, health, % cover..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <Grid size={18} />
          </button>
          <button
            className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            onClick={() => setViewMode("list")}
          >
            <List size={18} />
          </button>
        </div>

        <div className="status-filters">
          <button
            className={`filter-chip ${
              filterStatus === "All" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("All")}
          >
            All
          </button>
          <button
            className={`filter-chip ${
              filterStatus === "Prepared" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("Prepared")}
          >
            Weeds Present
          </button>
          <button
            className={`filter-chip ${
              filterStatus === "Not Prepared" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("Not Prepared")}
          >
            No Weeds
          </button>
        </div>
      </div>

      {/* Activities Grid */}
      <div className={`activities-${viewMode}`}>
        {filteredActivities.map((item) => (
          <div key={item.id} className="activity-card">
            <div className="activity-header">
              <div className="activity-crop">
                <span className="crop-icon">🌱</span>
                <span className="crop-name">Emergence</span>
              </div>
              <span
                className={`status-badge ${
                  item.weeds_present ? "prepared" : "not-prepared"
                }`}
              >
                {item.weeds_present ? "Weeds Present" : "Clean Field"}
              </span>
            </div>

            <div className="activity-location">
              <MapPin size={14} />
              <span>Farmer #{item.farmer} • Farm #{item.farm}</span>
            </div>

            <div className="activity-type">Weed Monitoring</div>

            <div className="activity-details">
              <div className="detail-row">
                <span className="detail-label">Weed Type</span>
                <span className="detail-value">{item.weed_type}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">% Cover</span>
                <span className="detail-value">{item.percentage_weed_cover}%</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Crop Health</span>
                <span className="detail-value">{item.crop_health}</span>
              </div>
            </div>

            <div className="activity-footer">
              <div className="activity-date">
                <Calendar size={14} />
                <strong>{new Date(item.created_at).toDateString()}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Emergence;
